import BaseAuthenticator from 'ember-simple-auth/authenticators/oauth2-password-grant';
import location from 'ember-simple-auth/utils/location';
import RSVP, { Promise } from 'rsvp';
import { service } from '@ember/service';
import { isBlank, isEmpty } from '@ember/utils';
import { run } from '@ember/runloop';
import { getOwner } from '@ember/application';

export default class OidcAuthenticator extends BaseAuthenticator {
  @service session;

  constructor(...args) {
    super(...args);
    this.serverTokenEndpoint = `${this.rootURL}token`;
  }
  
  get rootURL() {
    return getOwner(this).resolveRegistration('config:environment').rootURL;
  }

  async authenticate({
    code,
    state,
    client_id,
    authorization_endpoint,
    select_account,
  } = {}) {
    const redirect_uri = `${window.location.protocol}//${window.location.host}${this.rootURL}authorize`;

    if (isBlank(code)) {
      if (isBlank(authorization_endpoint) || isBlank(client_id)) {
        return RSVP.reject({
          error: 'bad_request',
          error_description: 'Authorization endpoint or client_id is missing',
        });
      }

      // Initial step in user authentication
      // Redirect to auth provider
      const randomState = this.getRandomState(20);
      sessionStorage.setItem(randomState, randomState);

      const url =
        authorization_endpoint +
        `?client_id=${client_id}` +
        `&response_type=code` +
        `&redirect_uri=${redirect_uri}` +
        `&response_mode=query` +
        `&scope=openid email profile offline_access` +
        (select_account ? `&prompt=select_account` : '') +
        `&state=${randomState}~${btoa(this.session.attemptedTransition?.targetName || 'login')}`;

      location.replace(url);
      return new Promise(() => {});
    }

    // Callback from auth provider
    // Exchange authorization code for access token and finally athenticate the session
    if (isBlank(state)) {
      return RSVP.reject({
        error: 'bad_request',
        error_description: 'State is missing',
      });
    }

    const randomState = state.split('~')[0];
    const savedRandomState = sessionStorage.getItem(randomState);
    sessionStorage.removeItem(randomState);
    if (savedRandomState !== randomState) {
      return RSVP.reject({
        error: 'bad_request',
        error_description: 'Invalid state value',
      });
    }

    const data = {
      grant_type: 'auth_code',
      auth_code: code,
      redirect_url: redirect_uri,
    };

    return new Promise((resolve, reject) => {
      this.makeRequest(this.serverTokenEndpoint, data).then(
        (response) => {
          run(() => {
            if (!this._validate(response)) {
              reject(new Error('access_token is missing in server response'));
            }

            const expiresAt = this._absolutizeExpirationTime(
              response.expires_in,
            );
            this._scheduleAccessTokenRefresh(
              response.expires_in,
              expiresAt,
              response.refresh_token,
            );
            if (!isEmpty(expiresAt)) {
              response.expires_at = expiresAt;
            }

            resolve(response);
          });
        },
        (response) => {
          run(null, reject, response);
        },
      );
    });
  }

  getRandomState(length) {
    const chars =
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const state = [];
    for (let i = 0; i < length; i++) {
      state.push(chars.charAt(Math.floor(Math.random() * chars.length)));
    }
    return state.join('');
  }
}
